/**
 * Enhanced Itinerary Scheduling Utilities
 * 
 * Provides conflict detection, route optimization, and smart scheduling
 */

import type { ItineraryItem } from '@/services/itinerary';
import type { LocationCoordinates } from '@/services/location';
import { shouldUseLiveRouteEstimates } from '@/config/mapboxCostPolicy';

export type ConflictType = 'time_overlap' | 'insufficient_travel_time' | 'same_location_different_time';

export type ItineraryConflict = {
  type: ConflictType;
  item1: ItineraryItem;
  item2: ItineraryItem;
  severity: 'error' | 'warning';
  message: string;
  suggestedFix?: {
    itemId: string;
    newStartTime?: string;
    newEndTime?: string;
    newStartDate?: Date;
  };
};

export type RouteOptimization = {
  fromItem: ItineraryItem;
  toItem: ItineraryItem;
  estimatedTravelTime: number; // in minutes
  distance?: number; // in kilometers
  suggestedDepartureTime: string; // HH:mm format
  warning?: string;
};

const formatMinutesShort = (minutes: number): string => {
  if (!Number.isFinite(minutes) || minutes <= 0) return '0m';
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

/**
 * Parse time string (HH:mm) to minutes since midnight
 */
const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Format minutes since midnight to time string (HH:mm)
 */
const minutesToTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

/**
 * Get the full datetime for an item (date + time)
 */
const getItemDateTime = (item: ItineraryItem): { start: Date; end: Date } | null => {
  if (!item.startTime || item.startTime === 'flexible') return null;
  
  const start = new Date(item.startDate);
  const [hours, minutes] = item.startTime.split(':').map(Number);
  start.setHours(hours, minutes, 0, 0);
  
  let end: Date;
  if (item.endTime) {
    end = new Date(item.startDate);
    const [endHours, endMinutes] = item.endTime.split(':').map(Number);
    end.setHours(endHours, endMinutes, 0, 0);
  } else {
    // Default to 60 minutes if no end time (prevents false "conflicts" for back-to-back items).
    end = new Date(start);
    end.setMinutes(end.getMinutes() + 60);
  }
  
  return { start, end };
};

/**
 * Check if two items overlap in time
 */
const itemsOverlap = (item1: ItineraryItem, item2: ItineraryItem): boolean => {
  // Only check if they're on the same date
  const date1 = item1.startDate.toISOString().split('T')[0];
  const date2 = item2.startDate.toISOString().split('T')[0];
  if (date1 !== date2) return false;
  
  // Both need times to check overlap
  if (!item1.startTime || !item2.startTime) return false;
  
  const dt1 = getItemDateTime(item1);
  const dt2 = getItemDateTime(item2);
  
  if (!dt1 || !dt2) return false;
  
  // Check if time ranges overlap
  return dt1.start < dt2.end && dt2.start < dt1.end;
};

/**
 * Calculate estimated travel time between two locations (in minutes)
 * Uses caching, supports multiple travel modes, traffic-aware routing, and batch processing
 */
export const estimateTravelTime = async (
  fromLocation: string | undefined,
  toLocation: string | undefined,
  options: {
    mode?: 'walking' | 'driving' | 'transit';
    departureTime?: Date;
    defaultTime?: number;
    country?: string;
    proximity?: LocationCoordinates;
  } = {}
): Promise<number> => {
  const { mode = 'driving', departureTime, defaultTime = 30, country, proximity } = options;

  if (!fromLocation || !toLocation) {
    return defaultTime;
  }
  
  // If locations are the same, return 0
  if (fromLocation.toLowerCase().trim() === toLocation.toLowerCase().trim()) {
    return 0;
  }
  
  try {
    // Check cache first
    const { getCachedTravelTime, cacheTravelTime } = await import('@/utils/travelTimeCache');
    const cached = await getCachedTravelTime(fromLocation, toLocation, mode, departureTime, country);
    if (cached !== null) {
      return cached;
    }

    if (shouldUseLiveRouteEstimates()) {
      // Try to use Mapbox Directions API for accurate travel time
      const { geocodeAddress } = await import('@/services/maps');
      const { calculateRoute } = await import('@/services/navigation');
      
      // Geocode both addresses to get coordinates
      const fromCoords = await geocodeAddress(fromLocation, { country, proximity });
      const toCoords = await geocodeAddress(toLocation, { country, proximity });
      
      if (fromCoords && toCoords) {
        // Calculate route with traffic-aware routing if departure time provided
        const route = await calculateRoute(
          { latitude: fromCoords.coordinates[1], longitude: fromCoords.coordinates[0] },
          { latitude: toCoords.coordinates[1], longitude: toCoords.coordinates[0] },
          mode,
          departureTime
        );
        
        if (route) {
          // Convert seconds to minutes
          const travelTime = Math.ceil(route.duration / 60);
          
          // Cache the result
          await cacheTravelTime(
            fromLocation,
            toLocation,
            mode,
            travelTime,
            route.distance,
            departureTime,
            country
          );
          
          return travelTime;
        }
      }
    }
  } catch (error) {
    console.warn('[Itinerary Scheduling] Error calculating travel time via API, using heuristic:', error);
  }
  
  // If API fails (or Mapbox token isn't configured), try using cached geocodes to derive a distance-based estimate.
  // This makes estimates adapt as the user adds/edits locations (once those locations have been geocoded elsewhere in the app).
  try {
    const [{ getCachedGeocode }, { haversineDistance }] = await Promise.all([
      import('@/utils/geocodeCache'),
      import('@/utils/geocodeHelpers'),
    ]);
    const [fromCached, toCached] = await Promise.all([
      getCachedGeocode(fromLocation),
      getCachedGeocode(toLocation),
    ]);

    if (fromCached?.coordinates && toCached?.coordinates) {
      const distKm = haversineDistance(
        { lat: fromCached.coordinates[1], lng: fromCached.coordinates[0] },
        { lat: toCached.coordinates[1], lng: toCached.coordinates[0] },
      );

      if (Number.isFinite(distKm) && distKm > 0) {
        // Reasonable urban speeds (km/h). Add a small fixed overhead for parking/waiting.
        const overheadMin = mode === 'walking' ? 2 : 6;
        const speedKmh = mode === 'walking' ? 4.8 : mode === 'transit' ? 20 : 32;
        const minutes = Math.ceil((distKm / speedKmh) * 60 + overheadMin);
        // Guardrails: avoid extreme values from bad coords.
        const clamped = Math.max(1, Math.min(180, minutes));
        return clamped;
      }
    }
  } catch {
    // ignore: fall back to string-based heuristic below
  }

  // Improved string-based heuristic when API/cache fails
  return estimateTravelTimeFallback(fromLocation, toLocation, mode, defaultTime);
};

/**
 * Improved fallback heuristic for travel time estimation
 * Uses location analysis to provide better estimates than a flat default
 */
const estimateTravelTimeFallback = (
  fromLocation: string,
  toLocation: string,
  mode: 'walking' | 'driving' | 'transit',
  defaultTime: number
): number => {
  const from = fromLocation.toLowerCase().trim();
  const to = toLocation.toLowerCase().trim();
  
  // If one contains the other, they're likely very close
  const directMatch = from.includes(to) || to.includes(from);
  if (directMatch) {
    return mode === 'walking' ? 10 : 5;
  }
  
  // Extract neighborhood/area names (common patterns)
  const extractArea = (loc: string): string[] => {
    const areas: string[] = [];
    // Common neighborhood/area patterns
    const patterns = [
      // Before comma usually means neighborhood/area
      /^([^,]+),/,
      // Common location prefixes
      /(?:near|in|at)\s+([^,]+)/i,
    ];
    for (const pattern of patterns) {
      const match = loc.match(pattern);
      if (match) areas.push(match[1].toLowerCase().trim());
    }
    // Also add full string split by common delimiters
    const parts = loc.split(/[,\/\-&]/).map(p => p.trim().toLowerCase()).filter(Boolean);
    areas.push(...parts);
    return [...new Set(areas)];
  };
  
  const fromAreas = extractArea(from);
  const toAreas = extractArea(to);
  
  // Check if any areas overlap (same neighborhood/district)
  const sameArea = fromAreas.some(fa => toAreas.some(ta => 
    fa === ta || fa.includes(ta) || ta.includes(fa)
  ));
  
  if (sameArea) {
    // Same neighborhood - short trip
    switch (mode) {
      case 'walking': return 15;
      case 'driving': return 10;
      case 'transit': return 15;
      default: return 10;
    }
  }
  
  // Check for common city indicators (both in same city)
  const cityIndicators = [
    'downtown', 'midtown', 'uptown', 'center', 'central',
    'east', 'west', 'north', 'south',
    'district', 'quarter', 'village', 'heights', 'park',
  ];
  
  const hasFromCity = cityIndicators.some(ind => from.includes(ind));
  const hasToCity = cityIndicators.some(ind => to.includes(ind));
  
  // If both locations have city neighborhood indicators, likely same city
  if (hasFromCity && hasToCity) {
    // Different neighborhoods in same city - moderate trip
    switch (mode) {
      case 'walking': return 25;
      case 'driving': return 15;
      case 'transit': return 20;
      default: return 15;
    }
  }
  
  // Check for airport/station (longer trips)
  const isTransportHub = (loc: string): boolean => {
    const hubKeywords = ['airport', 'station', 'terminal', 'port', 'hub'];
    return hubKeywords.some(kw => loc.includes(kw));
  };
  
  if (isTransportHub(from) || isTransportHub(to)) {
    // Trips to/from transport hubs are usually longer
    switch (mode) {
      case 'walking': return 60; // Long walk, probably not walking
      case 'driving': return 30;
      case 'transit': return 45;
      default: return 30;
    }
  }
  
  // Default fallback based on mode
  switch (mode) {
    case 'walking': return Math.min(defaultTime, 20); // Walking fallback shouldn't be too long
    case 'driving': return defaultTime;
    case 'transit': return Math.round(defaultTime * 1.2); // Transit usually slightly longer
    default: return defaultTime;
  }
};

/**
 * Detect conflicts in itinerary items
 * Note: This is async because it uses Mapbox API for accurate travel time calculation
 * @param preferredTravelMode Optional travel mode preference (walking, driving, transit)
 */
export const detectConflicts = async (
  items: ItineraryItem[],
  preferredTravelMode?: 'walking' | 'driving' | 'transit'
): Promise<ItineraryConflict[]> => {
  const conflicts: ItineraryConflict[] = [];
  
  // Group items by date
  const itemsByDate = new Map<string, ItineraryItem[]>();
  items.forEach(item => {
    const dateKey = item.startDate.toISOString().split('T')[0];
    if (!itemsByDate.has(dateKey)) {
      itemsByDate.set(dateKey, []);
    }
    itemsByDate.get(dateKey)!.push(item);
  });
  
  // Check conflicts within each date
  for (const [dateKey, dateItems] of itemsByDate.entries()) {
    // Sort by start time
    const sortedItems = dateItems
      .filter(item => item.startTime && item.startTime !== 'flexible')
      .sort((a, b) => {
        if (!a.startTime || !b.startTime) return 0;
        return timeToMinutes(a.startTime) - timeToMinutes(b.startTime);
      });
    
    // Check each pair for conflicts
    for (let i = 0; i < sortedItems.length; i++) {
      for (let j = i + 1; j < sortedItems.length; j++) {
        const item1 = sortedItems[i];
        const item2 = sortedItems[j];
        
        // Check for time overlap (synchronous check)
        if (itemsOverlap(item1, item2)) {
          const dt1 = getItemDateTime(item1);
          const dt2 = getItemDateTime(item2);
          const overlapHint =
            dt1 && dt2
              ? (() => {
                  const overlapStart = dt1.start > dt2.start ? dt1.start : dt2.start;
                  const overlapEnd = dt1.end < dt2.end ? dt1.end : dt2.end;
                  const mins = Math.round((overlapEnd.getTime() - overlapStart.getTime()) / (1000 * 60));
                  return mins > 0 ? ` (${formatMinutesShort(mins)} overlap)` : '';
                })()
              : '';
          conflicts.push({
            type: 'time_overlap',
            item1,
            item2,
            severity: 'error',
            message: `Time overlap${overlapHint}: “${item1.title}” and “${item2.title}”`,
            suggestedFix: {
              itemId: item2.id,
              newStartTime: (() => {
                if (!dt1) return undefined;
                const newStart = new Date(dt1.end);
                newStart.setMinutes(newStart.getMinutes() + 15); // Add 15 min buffer
                return minutesToTime(newStart.getHours() * 60 + newStart.getMinutes());
              })(),
            },
          });
        }
        
        // Check for insufficient travel time (async check using Mapbox API with traffic-aware routing)
        const dt1 = getItemDateTime(item1);
        const dt2 = getItemDateTime(item2);
        
        if (dt1 && dt2 && dt1.end < dt2.start) {
          // Use departure time (when item1 ends) for traffic-aware routing
          // Note: preferredTravelMode should be passed from the calling function
          const travelTime = await estimateTravelTime(
            item1.address || item1.location,
            item2.address || item2.location,
            {
              mode: preferredTravelMode || 'driving', // Use trip's preferred mode or default to driving
              departureTime: dt1.end, // Traffic-aware: use actual departure time
            }
          );
          const timeBetween = (dt2.start.getTime() - dt1.end.getTime()) / (1000 * 60); // minutes
          
          if (timeBetween < travelTime) {
            const need = formatMinutesShort(travelTime);
            const have = formatMinutesShort(Math.round(timeBetween));
            const modeLabel = preferredTravelMode ? preferredTravelMode : 'driving';
            conflicts.push({
              type: 'insufficient_travel_time',
              item1,
              item2,
              severity: 'warning',
              message: `Travel time issue (${modeLabel}): need ${need}, only ${have} available (${item1.title} → ${item2.title})`,
              suggestedFix: {
                itemId: item2.id,
                newStartTime: (() => {
                  const newStart = new Date(dt1.end);
                  newStart.setMinutes(newStart.getMinutes() + travelTime + 15); // Add travel time + 15 min buffer
                  return minutesToTime(newStart.getHours() * 60 + newStart.getMinutes());
                })(),
              },
            });
          }
        }
      }
    }
  }
  
  return conflicts;
};

/**
 * Optimize route between itinerary items
 * @param preferredTravelMode Optional travel mode preference
 */
export const optimizeRoute = async (
  items: ItineraryItem[],
  date: Date,
  preferredTravelMode?: 'walking' | 'driving' | 'transit'
): Promise<RouteOptimization[]> => {
  const optimizations: RouteOptimization[] = [];
  
  // Get items for the specific date
  const dateKey = date.toISOString().split('T')[0];
  const dateItems = items
    .filter(item => {
      const itemDateKey = item.startDate.toISOString().split('T')[0];
      return itemDateKey === dateKey && item.startTime;
    })
    .sort((a, b) => {
      if (!a.startTime || !b.startTime) return 0;
      return timeToMinutes(a.startTime) - timeToMinutes(b.startTime);
    });
  
  // Calculate travel time between consecutive items
  for (let i = 0; i < dateItems.length - 1; i++) {
    const fromItem = dateItems[i];
    const toItem = dateItems[i + 1];
    
    const fromDt = getItemDateTime(fromItem);
    const toDt = getItemDateTime(toItem);
    
    if (!fromDt || !toDt) continue;
    
    const travelTime = await estimateTravelTime(
      fromItem.address || fromItem.location,
      toItem.address || toItem.location,
      {
        mode: preferredTravelMode || 'driving',
        departureTime: fromDt.end, // Traffic-aware routing
      }
    );
    
    const timeBetween = (toDt.start.getTime() - fromDt.end.getTime()) / (1000 * 60);
    
    // Calculate suggested departure time
    const suggestedDeparture = new Date(fromDt.end);
    suggestedDeparture.setMinutes(suggestedDeparture.getMinutes() + travelTime);
    
    let warning: string | undefined;
    if (timeBetween < travelTime) {
      warning = `Insufficient travel time. Need ${travelTime} minutes but only have ${Math.round(timeBetween)} minutes.`;
    }
    
    optimizations.push({
      fromItem,
      toItem,
      estimatedTravelTime: travelTime,
      suggestedDepartureTime: minutesToTime(suggestedDeparture.getHours() * 60 + suggestedDeparture.getMinutes()),
      warning,
    });
  }
  
  return optimizations;
};

/**
 * Generate smart scheduling suggestions
 * @param preferredTravelMode Optional travel mode preference from trip settings
 */
export const generateSchedulingSuggestions = async (
  items: ItineraryItem[],
  tripStartDate: Date,
  tripEndDate: Date,
  preferredTravelMode?: 'walking' | 'driving' | 'transit'
): Promise<{
  conflicts: ItineraryConflict[];
  optimizations: RouteOptimization[];
  suggestions: Array<{
    item: ItineraryItem;
    suggestedStartTime: string;
    suggestedEndTime: string;
    reason: string;
  }>;
}> => {
  const conflicts = await detectConflicts(items, preferredTravelMode);
  const optimizations: RouteOptimization[] = [];
  
  // Get all unique dates in the trip
  const dates = new Set<string>();
  items.forEach(item => {
    const dateKey = item.startDate.toISOString().split('T')[0];
    dates.add(dateKey);
  });
  
  // Optimize route for each date
  for (const dateStr of dates) {
    const date = new Date(dateStr);
    const dateOptimizations = await optimizeRoute(items, date, preferredTravelMode);
    optimizations.push(...dateOptimizations);
  }
  
  // Generate suggestions based on conflicts and optimizations
  const suggestions: Array<{
    item: ItineraryItem;
    suggestedStartTime: string;
    suggestedEndTime: string;
    reason: string;
  }> = [];
  
  conflicts.forEach(conflict => {
    if (conflict.suggestedFix) {
      const item = items.find(i => i.id === conflict.suggestedFix!.itemId);
      if (item && conflict.suggestedFix.newStartTime) {
        const dt = getItemDateTime(item);
        if (dt) {
          const suggestedEnd = new Date(dt.end);
          const duration = (dt.end.getTime() - dt.start.getTime()) / (1000 * 60); // duration in minutes
          
          const newStart = new Date(item.startDate);
          const [hours, mins] = conflict.suggestedFix.newStartTime.split(':').map(Number);
          newStart.setHours(hours, mins, 0, 0);
          
          const newEnd = new Date(newStart);
          newEnd.setMinutes(newEnd.getMinutes() + duration);
          
          suggestions.push({
            item,
            suggestedStartTime: conflict.suggestedFix.newStartTime,
            suggestedEndTime: minutesToTime(newEnd.getHours() * 60 + newEnd.getMinutes()),
            reason: conflict.message,
          });
        }
      }
    }
  });
  
  return {
    conflicts,
    optimizations,
    suggestions,
  };
};

/**
 * Apply suggested time to an item
 */
export const applySuggestion = (
  item: ItineraryItem,
  suggestedStartTime: string,
  suggestedEndTime?: string
): Partial<ItineraryItem> => {
  return {
    startTime: suggestedStartTime,
    endTime: suggestedEndTime,
  };
};

